import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faChevronDown, 
  faChevronUp, 
  faExternalLinkAlt,
  faRefresh
} from '@fortawesome/free-solid-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';

interface ServiceSectionProps {
  title: string;
  icon: IconDefinition;
  color: string;
  data: any;
  loading?: boolean;
  children: React.ReactNode;
  onRefresh?: () => void;
}

export const ServiceSection: React.FC<ServiceSectionProps> = ({
  title,
  icon,
  color,
  data,
  loading = false,
  children,
  onRefresh
}) => {
  const [isExpanded, setIsExpanded] = useState(true);

  if (loading) {
    return (
      <div className="bg-white border border-gray-300 rounded p-3 animate-pulse">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-2">
            <div className={`w-7 h-7 bg-${color}-100 rounded`}></div>
            <div className="h-5 bg-gray-200 rounded w-32"></div>
          </div>
          <div className="h-6 bg-gray-200 rounded w-20"></div>
        </div>
        <div className="space-y-2">
          <div className="h-3 bg-gray-200 rounded"></div>
          <div className="h-3 bg-gray-200 rounded w-3/4"></div>
          <div className="h-3 bg-gray-200 rounded w-1/2"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-300 rounded overflow-hidden">
      <div
        className="p-3 border-b border-gray-300 cursor-pointer hover:bg-gray-50"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div className={`w-7 h-7 bg-${color}-100 rounded flex items-center justify-center`}>
              <FontAwesomeIcon icon={icon} className={`text-${color}-600 text-sm`} />
            </div>
            <div>
              <h3 className="text-base font-bold text-gray-900">{title}</h3>
              <div className="text-xs text-gray-500">
                {data?.summary || 'Service data'}
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {onRefresh && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onRefresh();
                }}
                className="px-2 py-1 text-xs font-medium text-gray-600 bg-white border border-gray-300 hover:bg-gray-50 rounded"
              >
                <FontAwesomeIcon icon={faRefresh} className="mr-1" />
                Refresh
              </button>
            )}
            <button
              className="p-1.5 text-gray-400 hover:text-gray-600"
            >
              <FontAwesomeIcon
                icon={isExpanded ? faChevronUp : faChevronDown}
                className="text-xs"
              />
            </button>
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className="p-3">
          {children}
        </div>
      )}
    </div>
  );
};